<template>
  <form
    class="justify-center text-sm lowercase form cart-form sm:text-base md:justify-start"
  >
    <div class="cart-input-container">
      <div class="cart-quantity-container">
        <label>quantity</label>
        <number-input
          v-model="model.quantity"
          :min="1"
          :max="maxQuantity"
          :disabled="soldOut"
          class="cart-input--number input input--stacked"
        />
      </div>
      <div v-if="subscription" class="cart-input--options">
        <label class="cart-input__radio-label">purchase type</label>
        <recess-radio
          :label="singlePurchaseRadioLabel"
          :checked="!subscriptionSelected"
          value="single"
          class="cart-input--radio"
          @mouseover.native="showTooltip('single')"
          @mouseleave.native="hideTooltip('single')"
          @input="changeSelectedType('single')"
        />
        <recess-radio
          :label="subscribeRadioLabel"
          :notice="subscriptionNotice"
          :checked="subscriptionSelected"
          :interval-options="intervalOptions"
          :subscription-interval="model.subscriptionInterval"
          value="subscription"
          class="cart-input--radio mt-1/2em"
          @mouseover.native="showTooltip('subscription')"
          @mouseleave.native="hideTooltip('subscription')"
          @input="changeSelectedType('subscription')"
          @update:subscriptionInterval="onSubscriptionIntervalUpdate"
        />
      </div>
    </div>
    <div
      v-if="productVariant && !subscription"
      class="w-full text-center md:text-left cart-input--selected-price"
    >
      <div class="text-xl font-bold">
        {{ selectedVariantDetails }}
      </div>
    </div>
    <div
      v-if="!$apollo.queries.productVariant.loading && !soldOut"
      :key="`${model.selectedType}-${price}-${discountActive}`"
      class="flex flex-col items-start w-full md:items-center md:flex-row lg:flex-no-wrap"
    >
      <div class="cart-ctas">
        <div class="cart-input cart-input--cta">
          <cta
            class="w-full text-lg whitespace-no-wrap cta-with-price"
            fill
            @click.stop.prevent="debouncedSubmit"
          >
            <div class="cta-with-price__wrapper">
              <div class="cta-with-price__label">
                {{ subscriptionSelected ? 'Subscribe' : 'Add to Cart' }}
              </div>
              <div class="cta-with-price__price" v-html="priceLabel" />
            </div>
          </cta>
          <em
            v-if="shippingNotice"
            class="absolute left-0 top-full mt-1/2em ml-1/2em xs:ml-em"
          >
            {{ shippingNotice }}
          </em>
        </div>
        <buy-with-prime
          v-if="!subscriptionSelected && productVariant"
          :sku="productVariant.sku"
        />
        <a
          v-if="amazonLink"
          class="flex items-center justify-center w-full mt-4 cart-input__amazon-cta"
          :href="amazonLink"
          @click="trackAmazonAddToCart"
        >
          <img class="m-2" :src="amazonImage" />
          <em v-if="amazonCta" class="flex-1 text-xs">
            {{ amazonCta }}
          </em>
        </a>
      </div>
    </div>
    <div
      v-else-if="!$apollo.queries.productVariant.loading && soldOut"
      class="w-full"
    >
      <cta disabled>Sold Out</cta>
    </div>
  </form>
</template>

<script>
import debounce from 'lodash/debounce'
import { mapState, mapGetters, mapActions } from 'vuex'
import { VTooltip } from 'v-tooltip'
import extractShopifyId from '~/util/shopify/extract-shopify-id'
import formatLineItemSchema from '~/util/shopify/format-line-item-schema'
import formatLineItemAttributes from '~/util/shopify/format-line-item-attributes'
import getMaxVariantQuantity from '~/util/shopify/get-max-variant-quantity'
import roundNumber from '~/util/number/round'
import variantBySelectedOptionsQuery from '~/shopify/queries/variantBySelectedOptions.gql'
import NumberInput from '~/components/form/number-input.vue'
import BuyWithPrime from '~/components/shop/buy-with-prime.vue'
import RecessRadio from '~/components/form/recess-radio.vue'

export default {
  name: 'AddProductToCart',

  directives: {
    tooltip: VTooltip
  },

  components: {
    NumberInput,
    BuyWithPrime,
    RecessRadio
  },

  props: {
    id: {
      default: null,
      type: String
    },
    title: {
      default: null,
      type: String
    },
    path: {
      default: null,
      type: String
    },
    category: {
      default: null,
      type: String
    },
    options: {
      default: () => [],
      type: Array
    },
    defaultVariant: {
      default: null,
      type: Object
    },
    product: {
      default: null,
      type: Object
    },
    shippingNotice: {
      default: null,
      type: String
    },
    subscription: {
      default: null,
      type: Object
    },
    subscriptionSkus: {
      default: () => [],
      type: Array
    },
    subscriptionNotice: {
      default: "You're in control. Try, adjust, or cancel at anytime",
      type: String
    },
    isCanned: {
      type: Boolean,
      default: false
    },
    amazonLink: {
      type: String,
      required: false,
      default: undefined
    },
    amazonCta: {
      type: String,
      required: false,
      default: undefined
    }
  },

  data() {
    const options = this.options
    const selectedOptions = options.length && this.getDefaultOptions(options)
    const subscriptionInterval = this.subscription?.sellingPlans?.[0]?.id

    return {
      model: {
        selectedOptions,
        selectedType: null,
        subscriptionInterval,
        quantity: 1
      },
      isSubmitting: false,
      message: null,
      tooltip: null,
      buyWithPrimeSiteId: process.env.buyWithPrimeSiteId,
      buyWithPrimeWidgetId: process.env.buyWithPrimeWidgetId
    }
  },

  apollo: {
    productVariant: {
      client: 'cart',
      query: variantBySelectedOptionsQuery,
      variables() {
        return {
          id: this.id,
          selectedOptions: this.selectedOptions
        }
      },
      update({ node }) {
        return node && node.variantBySelectedOptions
      },
      skip() {
        const id = this.id
        const selectedOptions = this.selectedOptions
        return !id || !selectedOptions.length
      }
    }
  },

  computed: {
    ...mapState({
      googleClientId: state => state.googleClientId,
      cartId: state => state.storage.cartId,
      amazonImage: state => state.settings.amazonSettings?.image?.asset?.url,
      rebuyTracker: state => state.shop.rebuyTracker
    }),
    ...mapGetters({
      bp: 'screen/breakpoint',
      productsByCategory: 'shop/productsByCategory'
    }),
    /**
     * Returns the current selectedOptions in an array of
     * option name => option value pairs
     * @function
     * @returns {array}
     */
    selectedOptions() {
      const model = this.model.selectedOptions
      let options = []
      if (model) {
        const selectedOptions = Object.entries(model)
        options = selectedOptions.map(([name, value]) => ({ name, value }))
      }
      return options
    },
    selectedVariantDetails() {
      if (!this.productVariant) {
        return ''
      }
      const { price, title } = this.productVariant
      const amount = price.amount

      if (title === 'Default Title' || title === `$${roundNumber(amount)}`) {
        return `$${roundNumber(amount)}`
      }

      return `${title} - $${roundNumber(amount)}`
    },
    /**
     * Returns a subscription variant with sku matching
     * the product's sku (if one exists)
     * @function
     */
    subscriptionVariant() {
      const subscription = this.subscription
      const productVariant = this.productVariant
      let variant
      if (subscription && productVariant) {
        variant = subscription.variants.edges.find(({ node }) => {
          return node.sku === productVariant.sku
        })
        if (variant) {
          variant = variant.node
        }
      }
      return variant
    },
    intervalOptions() {
      if (this.subscription?.sellingPlans) {
        return this.subscription.sellingPlans.map(({ id, name }) => ({
          key: name,
          value: id
        }))
      }
      return []
    },
    subscriptionSelected() {
      return this.model.selectedType === 'subscription'
    },
    subscriptionOnly() {
      const subscription = this.subscriptionVariant
      const subscriptionSkus = this.subscriptionSkus
      let only = false
      if (
        !subscription ||
        !subscription.sku ||
        !subscriptionSkus ||
        !subscriptionSkus.length
      ) {
        return only
      }
      only = !!subscriptionSkus.find(sku => sku === subscription.sku)
      return only
    },
    singleOnly() {
      return !this.subscriptionVariant
    },
    price() {
      const productVariant = this.productVariant
      if (this.hasCompareAtDiscount) {
        return this.compareAtPrice
      }
      return productVariant?.price?.amount
    },
    compareAtPrice() {
      return this.productVariant?.compareAtPrice?.amount
    },
    hasCompareAtDiscount() {
      return (
        this.productVariant &&
        this.compareAtPrice > parseFloat(this.productVariant.price.amount)
      )
    },
    discountActive() {
      const { subscriptionVariant } = this
      const { selectedType } = this.model
      let discountActive

      if (subscriptionVariant && selectedType === 'subscription') {
        discountActive = parseFloat(subscriptionVariant?.price?.amount)
      } else if (this.hasCompareAtDiscount) {
        discountActive = parseFloat(this.productVariant?.price?.amount)
      }
      return discountActive
    },
    soldOut() {
      return !this.productVariant || !this.productVariant.availableForSale
    },
    filteredOptions() {
      return this.options.filter(o => o.name !== 'Title' && o.values.length > 1)
    },
    /**
     * Check that there is either more than one option or
     * no options with the Shopify default name "Title"
     * @function
     * @returns {boolean}
     */
    hasMultipleOptions() {
      const options = this.filteredOptions
      return options.length !== 1 || !options.find(o => o.name === 'Title')
    },
    /**
     * Returns all variants besides those that have the
     * currently selected sku.
     * @function
     * @returns {array}
     */
    otherProductVariants() {
      const selectedVariant = this.productVariant
      const productVariants = this.product && this.product.variants
      const hasMultipleOptions = this.hasMultipleOptions
      let variants = []
      if (!selectedVariant || !productVariants || !hasMultipleOptions) {
        return variants
      }
      if (productVariants.edges.length > 0) {
        variants = productVariants.edges
          .map(({ node }) => node)
          .filter(node => node.sku !== selectedVariant.sku)
      }
      return variants
    },
    maxQuantity() {
      const variant = this.productVariant
      if (!variant) {
        return undefined
      }
      const available = variant.availableForSale
      let quantity = variant.quantityAvailable
      if (available && quantity <= 0) {
        quantity = 9999
      } else if (!available) {
        quantity = 0
      }
      return quantity
    },
    variants() {
      const productVariants = this.product && this.product.variants
      return productVariants.edges.map(({ node }) => node)
    },
    singleProductVariant() {
      const subscriptionSkus = this.subscriptionSkus
      if (
        this.productVariant &&
        !subscriptionSkus.includes(this.productVariant.sku)
      ) {
        return this.productVariant
      }
      const singleProducts = this.variants.filter(
        v => !subscriptionSkus.includes(v.sku)
      )
      return singleProducts[0]
    },
    subscriptionProductVariant() {
      const subscription = this.subscription
      const subVariant = subscription.variants.edges.map(({ node }) => node)[0]
      const productVariant = this.variants.filter(
        v => v.sku === subVariant.sku
      )[0]

      if (
        productVariant &&
        subVariant?.price !== productVariant?.price?.amount
      ) {
        productVariant.discountedPrice = subVariant?.price
      }

      return productVariant
    },
    singlePurchaseTitle() {
      if (this.singleProductVariant.title === 'Default Title') {
        return ''
      }

      return this.singleProductVariant.title
    },
    subscribeTitle() {
      if (this.subscriptionProductVariant?.title === 'Default Title') {
        return ''
      }

      return this.subscriptionProductVariant?.title
    },
    singlePurchasePrice() {
      return `$${roundNumber(this.singleProductVariant?.price?.amount) || ''}`
    },
    subscribePurchasePrice() {
      if (this.subscriptionProductVariant?.discountedPrice) {
        return `<del class='cta-with-price__price--striked'>$${roundNumber(
          this.subscriptionProductVariant?.price?.amount
        )}</del> $${roundNumber(
          this.subscriptionProductVariant?.discountedPrice?.amount
        )}`
      } else {
        return `$${roundNumber(this.subscriptionProductVariant?.price?.amount)}`
      }
    },
    priceLabel() {
      if (this.subscriptionSelected) {
        return this.subscribePurchasePrice
      } else {
        return this.singlePurchasePrice
      }
    },
    singlePurchaseRadioLabel() {
      if (this.singlePurchaseTitle) {
        return `single purchase: ${this.singlePurchaseTitle}`
      } else {
        return `single purchase`
      }
    },
    subscribeRadioLabel() {
      if (this.subscribeTitle) {
        return `subscription: ${this.subscribeTitle}`
      } else {
        return `subscription`
      }
    }
  },

  watch: {
    'model.quantity': {
      handler(newValue) {
        if (newValue === undefined || newValue === null) {
          this.model.quantity = 1
        }
      },
      immediate: true
    },
    options(options) {
      // set the default selected options
      options = this.getDefaultOptions(options)
      this.$set(this.model, 'selectedOptions', options)
    },
    subscriptionVariant(variant) {
      // set the default product type
      const selectedType =
        variant && this.subscriptionOnly ? 'subscription' : 'single'
      this.$set(this.model, 'selectedType', selectedType)
    },
    'model.selectedType'(selected) {
      if (!this.subscription) {
        return
      }
      const single = selected === 'single'
      const singleOnly = this.singleOnly
      const subscription = selected === 'subscription'
      const subscriptionOnly = this.subscriptionOnly
      // if the selected type mismatches with the selected
      // sku's subscription-only specification (or a subscription
      // variant does not exist), then try to select another
      // variant with a different sku
      if (single && subscriptionOnly) {
        this.changeSingleSku()
      } else if (subscription && singleOnly) {
        this.changeSubscriptionSku()
      }
    },
    productVariant(variant) {
      const product = this.product

      this.$gtm.push({
        event: 'Product Viewed',
        google_client_id: this.googleClientId,
        product_id: product.id,
        sku: variant.sku,
        name: product.title,
        variant: variant.title,
        price: variant.price.amount,
        quantity: this.model.quantity,
        value: variant.price.amount,
        url: `${process.env.canonicalUrl}${this.$route.path}`
      })
    }
  },

  created() {
    this.debouncedSubmit = debounce(
      this.submit,
      process.env.shopifyRequestDebounce
    )
    // clear rebuy tracker on component cleanup
    this.$on('hook:beforeDestroy', () => {
      if (this.product.id === this.rebuyTracker) {
        this.$store.dispatch('shop/clearRebuyTracker')
      }
    })
  },

  methods: {
    ...mapActions({
      addToCart: 'shop/addToCart'
    }),
    onSubscriptionIntervalUpdate(subscriptionInterval, a, b, c) {
      this.model.subscriptionInterval = subscriptionInterval
    },
    /**
     * Returns an Object of name => value pairs from an
     * array of options, either from the first value of
     * each the product's options or from a specific
     * variant's options (defaultVariant)
     * @function
     * @param {array} options - The product's options array
     * @returns {object} - key: option name, value: option value
     */
    getDefaultOptions(options) {
      const defaultVariant = this.defaultVariant
      if (defaultVariant) {
        return this.getVariantOptions(defaultVariant)
      }
      return options
        .map(o => ({ [o.name]: o.values[0] }))
        .reduce((a, b) => Object.assign(a, b), {})
    },
    /**
     * Returns an Object of name => value pairs from a
     * given variant's array of options
     * @function
     * @param {object} variant
     * @returns {object} - key: option name, value: option value
     */
    getVariantOptions(variant) {
      if (variant) {
        return variant.selectedOptions
          .map(o => ({ [o.name]: o.value }))
          .reduce((a, b) => Object.assign(a, b), {})
      }
    },
    /**
     * Formats and adds the current product or subscription
     * variant to the user's checkout as a line item based on
     * the current product type
     * @function
     */
    async submit() {
      if (this.isSubmitting) {
        return
      }
      this.isSubmitting = true

      const product = this.product
      const subscription = this.subscription
      const productVariant = this.productVariant
      const subscriptionVariant = this.subscriptionVariant
      const type = this.model.selectedType || 'single'
      const isSubscription = type === 'subscription'
      const selectedProduct = isSubscription ? subscription : product
      const variant = isSubscription ? subscriptionVariant : productVariant
      const quantity = this.model.quantity
      const soldOut = this.soldOut
      const maxVariantQuantity = getMaxVariantQuantity(variant, quantity)
      const maxQuantity =
        maxVariantQuantity <= 0 && !soldOut ? quantity : maxVariantQuantity

      if (quantity !== maxQuantity) {
        this.newMessage(
          'We updated your cart with the last remaining items we have in stock'
        )
      }

      const sellingPlan = subscription?.sellingPlans?.find(
        ({ id }) => id === this.model.subscriptionInterval
      )

      const attributes = formatLineItemAttributes({
        isRebuyProductDiscovery: this.rebuyTracker === product.id
      })

      const line = formatLineItemSchema(
        {
          ...selectedProduct,
          variant,
          attributes,
          productType: selectedProduct.productType || product.productType,
          sellingPlan: isSubscription && sellingPlan
        },
        'product'
      )

      line.node.quantity = maxQuantity
      await this.addToCart({ edge: line, additive: true })

      this.trackAdded({
        cart: this.cartId,
        id: product.id,
        decodedId: extractShopifyId(product.id),
        category: this.category,
        sku: variant.sku,
        name: product.title,
        variant: variant.title,
        price: variant.price.amount,
        quantity: maxQuantity,
        url: `${process.env.canonicalUrl}${this.$route.path}`,
        image: variant.image?.transformedSrc,
        discount: subscriptionVariant ? subscriptionVariant.price : undefined
      })

      this.isSubmitting = false
    },
    changeSelectedType(type) {
      this.$set(this.model, 'selectedType', type)
    },
    /**
     * Change the selected options to another single-only
     * variant's with a sku that differs from the current
     * variant's and display a notice
     * @function
     */
    changeSingleSku() {
      const subscriptionSkus = this.subscriptionSkus
      const otherVariants = this.otherProductVariants.filter(
        v => !subscriptionSkus.includes(v.sku)
      )
      if (otherVariants.length > 0) {
        const otherOptions = this.getVariantOptions(otherVariants[0])
        this.$set(this.model, 'selectedOptions', otherOptions)
      }
    },
    /**
     * Change the selected options to another subscription-only
     * variant's with a sku that differs from the current
     * variant's and display a notice
     * @function
     */
    changeSubscriptionSku() {
      const subscriptionSkus = this.subscriptionSkus
      const otherVariants = this.otherProductVariants.filter(v =>
        subscriptionSkus.includes(v.sku)
      )
      if (otherVariants.length > 0) {
        const otherOptions = this.getVariantOptions(otherVariants[0])
        this.$set(this.model, 'selectedOptions', otherOptions)
      }
    },
    newMessage(message) {
      if (this.message) {
        this.message.goAway()
      }
      this.message = this.$toast.global.cartInfo({ message })
    },
    showTooltip(name) {
      this.tooltip = name
    },
    hideTooltip(name) {
      if (this.tooltip === name) {
        this.tooltip = null
      }
    },
    trackAmazonAddToCart(e) {
      try {
        const product = this.product
        const productVariant = this.productVariant
        const subscriptionVariant = this.subscriptionVariant
        const type = this.model.selectedType || 'single'
        const isSubscription = type === 'subscription'
        const variant = isSubscription ? subscriptionVariant : productVariant

        this.$gtm.push({
          event: 'Add To Cart',
          google_client_id: this.googleClientId,
          cart_id: this.cartId,
          product_id: product.id,
          product_id_shopify: extractShopifyId(product.id),
          sku: variant.sku,
          name: product.title,
          variant: variant.title,
          price: variant.price.amount,
          quantity: 1,
          url: `${process.env.canonicalUrl}${this.$route.path}`,
          content_name: document.title,
          content_category: 'amazon product page',
          content_ids: [product.id],
          content_id: product.id,
          content_type: 'product',
          currency: 'USD'
        })
      } catch (e) {
        console.log("Couldn't send 'Add To Cart' event.")
      }
    },
    trackAdded(added) {
      this.$gtm.push({
        event: 'Add To Cart',
        google_client_id: this.googleClientId,
        cart_id: added.checkout,
        product_id: added.id,
        product_id_shopify: added.decodedId,
        sku: added.sku,
        name: added.name,
        variant: added.variant,
        price: added.price.amount,
        quantity: added.quantity,
        url: added.url
      })
      // klaviyo
      var klaviyo = window.klaviyo || [] // eslint-disable-line
      klaviyo.push([
        'track',
        'Added to Cart',
        {
          Brand: 'Recess',
          Categories: [added.category],
          CompareAtPrice: added.discount,
          ImageURL: added.image,
          Name: added.name,
          Price: added.price.amount,
          ProductID: added.id,
          URL: added.url
        }
      ])
    }
  }
}
</script>

<style>
.cart-form {
  --cart-gutter-x: 1rem;
  --cart-gutter-y: 2rem;
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  left: calc(-1 * var(--cart-gutter-x));
  padding-left: var(--cart-gutter-x);
  position: relative;
  width: calc(100% + var(--cart-gutter-x));
}

.cart-input {
  flex: 1 1 50%;
  margin-bottom: var(--cart-gutter-y);
  margin-right: var(--cart-gutter-x);
  max-width: 100%;
}

.cart-input-container {
  width: 100%;
}

.cart-quantity-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--cart-gutter-y);
  width: 100%;

  label {
    align-self: center;
    font-weight: 500;
  }
}

.cart-input--radio {
  position: relative;
  z-index: 2;
}

.cart-input--options {
  margin-bottom: calc(var(--cart-gutter-y) / 2);
  margin-right: var(--cart-gutter-x);
  width: 100%;
}

.cart-input__radio-label {
  display: block;
  font-weight: 500;
  margin-bottom: calc(var(--cart-gutter-y) / 3);
}

.cart-input__amazon-cta {
  align-self: center;
  margin: 1em;
}

.cart-input__amazon-cta img {
  height: 30px;
  width: auto;
}

.cart-input--number {
  flex: 0 1 35%;
}

.cart-input--radios {
  flex: 1 1 auto;
  max-width: none;
}

.cart-input--price {
  @apply text-3xl font-medium;
  --cart-gutter-x: 2rem;
  display: inline-flex;
  flex: 0 1 auto;
  justify-content: flex-end;
  min-width: 5em;
  position: relative;
  text-align: right;
  top: -0.1em;
}

.cart-ctas {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.cart-input--cta {
  display: flex;
  flex: 1 0 100%;
  margin-right: 0;
  padding-right: 0 !important;
  padding-top: 1rem;
  position: relative;
}

.cta-with-price {
  text-align: left;

  .cta__content {
    padding-left: var(--cart-gutter-x);
    padding-right: var(--cart-gutter-x);
  }
}

.cta-with-price__wrapper {
  display: flex;
  justify-content: space-between;

  .cta-with-price__price {
    .cta-with-price__price--striked {
      font-size: 0.8em;
      font-weight: normal;
      margin-right: 10px;
      opacity: 0.75;
    }
  }
}

.cart-input__helper {
  font-size: theme('fontSize.sm');
  font-weight: theme('fontWeight.normal');
  left: 0;
  margin-top: 0.6em;
  position: absolute;
  text-align: right;
  top: 100%;
  width: 100%;
}

.cart-input--selected-price {
  margin-bottom: var(--cart-gutter-y);
  padding-top: 1rem;
}

@screen sm {
  .cart-input--options {
    min-width: 50%;
  }

  .cart-input--selected-price {
    padding-left: var(--input-gutter);
  }

  .cart-input--cta {
    align-items: center;
    flex-direction: column;
  }

  .cart-input--price {
    justify-content: flex-end;
  }
}

@screen md {
  .cart-form {
    margin-left: auto;
    margin-right: auto;
    max-width: 640px;
  }

  .cart-input-container {
    align-items: flex-start;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .cart-quantity-container {
      flex-direction: column;
      justify-content: flex-end;
      order: 2;
      width: auto;

      label {
        align-self: start;
        margin-bottom: calc(var(--cart-gutter-y) / 3);
      }
    }

    .cart-input--options {
      order: 1;
      width: 60%;
    }
  }
}

@screen lg {
  .cart-form {
    margin-left: 0;
    margin-right: 0;
  }

  .cart-input-container {
    gap: 1em;
    justify-content: flex-start;
  }

  .cart-input {
    --cart-gutter-x: 2rem;
    flex: 0 1 25%;
  }

  .cart-input--options {
    --cart-gutter-x: 2rem;
  }

  .cart-input--number,
  .cart-input--dropdown,
  .cart-input--radios,
  .cart-input--options {
    flex: 0 1 auto;
  }

  .cart-input--price,
  .cart-input--cta {
    max-width: none !important;
  }

  .cart-input--price {
    flex: 0 1 auto !important;
  }

  .cart-ctas {
    width: 60%;
  }
}

@screen xl {
  .cart-input--radios {
    max-width: none;
    min-width: 0;
  }
}
</style>
